import { jsPDF } from 'jspdf';

// ─── Fix de acentos (mesmo padrão do generateInvoicePdf) ──────────────────────
function safe(text) {
  if (!text) return '';
  return String(text)
    .replace(/[àáâãä]/g, 'a').replace(/[ÀÁÂÃÄ]/g, 'A')
    .replace(/[èéêë]/g,  'e').replace(/[ÈÉÊË]/g,  'E')
    .replace(/[ìíîï]/g,  'i').replace(/[ÌÍÎÏ]/g,  'I')
    .replace(/[òóôõö]/g, 'o').replace(/[ÒÓÔÕÖ]/g, 'O')
    .replace(/[ùúûü]/g,  'u').replace(/[ÙÚÛÜ]/g,  'U')
    .replace(/[ç]/g,     'c').replace(/[Ç]/g,     'C')
    .replace(/[ñ]/g,     'n').replace(/[Ñ]/g,     'N')
    .replace(/[–—]/g,    '-')
    .replace(/['']/g,    "'")
    .replace(/[""]/g,    '"')
    .replace(/[^\x00-\xFF]/g, '');
}

// ─── Paleta de cores — idêntica ao generateInvoicePdf ─────────────────────────
const COLORS = {
  primary:     [22,  58, 114],
  primaryDark: [14,  38,  80],
  accent:      [37, 137, 210],
  accentLight: [90, 170, 230],
  light:       [240, 246, 255],
  lightAlt:    [248, 251, 255],
  dark:        [20,  20,  20],
  muted:       [110, 118, 135],
  white:       [255, 255, 255],
  border:      [210, 220, 235],
  success:     [39, 174,  96],
  warning:     [230, 160,   0],
  danger:      [200,  50,  50],
  gold:        [212, 175,  55],
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
function setColor(doc, color, target = 'text') {
  if (target === 'text') doc.setTextColor(...color);
  else if (target === 'fill') doc.setFillColor(...color);
  else if (target === 'draw') doc.setDrawColor(...color);
}

function truncate(doc, text, maxWidth) {
  if (doc.getTextWidth(text) <= maxWidth) return text;
  while (text.length > 0 && doc.getTextWidth(text + '...') > maxWidth) {
    text = text.slice(0, -1);
  }
  return text + '...';
}

/** Converte URL (Cloudinary ou qualquer http) para base64 */
async function urlToBase64(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Cabeçalho de seção — mesmo estilo do generateInvoicePdf */
function sectionHeader(doc, title, y, pageW, margin = 40) {
  const h = 24;

  setColor(doc, COLORS.accent, 'fill');
  doc.rect(margin, y, 4, h, 'F');

  setColor(doc, COLORS.light, 'fill');
  doc.rect(margin + 4, y, pageW - margin * 2 - 4, h, 'F');

  setColor(doc, COLORS.border, 'draw');
  doc.setLineWidth(0.3);
  doc.line(margin, y + h, pageW - margin, y + h);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  setColor(doc, COLORS.primary);
  doc.text(safe(title).toUpperCase(), margin + 12, y + 16);

  return y + h + 14;
}

/** Desenha cabeçalho da página (repetido em todas as páginas) */
function drawPageHeader(doc, user, pageW, logoBase64) {
  const headerH = logoBase64 ? 90 : 72;

  setColor(doc, COLORS.primary, 'fill');
  doc.rect(0, 0, pageW, headerH, 'F');

  setColor(doc, COLORS.accent, 'fill');
  doc.rect(0, headerH - 4, pageW, 4, 'F');

  const margin = 40;

  if (logoBase64) {
    const logoSize = 64;
    const logoX    = margin;
    const logoY    = (headerH - 4 - logoSize) / 2;

    setColor(doc, COLORS.white, 'fill');
    doc.roundedRect(logoX - 3, logoY - 3, logoSize + 6, logoSize + 6, 4, 4, 'F');
    doc.addImage(logoBase64, 'JPEG', logoX, logoY, logoSize, logoSize);

    const textX = logoX + logoSize + 16;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    setColor(doc, COLORS.white);
    doc.text('CATALOGO DE PRODUTOS', textX, 34);

    if (user.name) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      setColor(doc, COLORS.accentLight);
      doc.text(safe(user.name), textX, 52);
    }
    if (user.cnpj) {
      doc.setFontSize(10);
      setColor(doc, [180, 200, 230]);
      doc.text(`CNPJ: ${safe(user.cnpj)}`, textX, 68);
    }
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    setColor(doc, COLORS.white);
    doc.text('CATALOGO DE PRODUTOS', margin, 36);

    if (user.name) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(13);
      setColor(doc, COLORS.accentLight);
      doc.text(safe(user.name), margin, 54);
    }
    if (user.cnpj) {
      doc.setFontSize(10);
      setColor(doc, [180, 200, 230]);
      doc.text(`CNPJ: ${safe(user.cnpj)}`, margin, 68);
    }
  }

  return headerH;
}

/** Desenha rodapé da página */
function drawPageFooter(doc, user, pageW, pageH, pageNum, totalPages) {
  const margin = 40;
  const colMid = pageW / 2;

  setColor(doc, COLORS.accent, 'fill');
  doc.rect(0, pageH - 40, pageW, 2, 'F');

  setColor(doc, COLORS.light, 'fill');
  doc.rect(0, pageH - 38, pageW, 38, 'F');

  const now = new Date();
  const footerLeft  = safe(user.name || '');
  const footerRight = `Gerado em ${now.toLocaleDateString('pt-BR')} as ${now.toLocaleTimeString('pt-BR')}`;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setColor(doc, COLORS.primary);
  if (footerLeft) doc.text(footerLeft, margin, pageH - 18);

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8.5);
  setColor(doc, COLORS.muted);
  doc.text(footerRight, pageW - margin, pageH - 18, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  setColor(doc, COLORS.muted);
  doc.text(`Pagina ${pageNum} de ${totalPages}`, colMid, pageH - 10, { align: 'center' });
}

// ─── Geração do catálogo ──────────────────────────────────────────────────────
/**
 * Gera e baixa um PDF com o catálogo de produtos do estoque.
 * @param {Array} products - lista de produtos (do ManageProducts)
 * @param {Object} user    - usuário logado (nome, cnpj, imagem, telefone, etc.)
 */
export async function generateCatalogPdf(products, user) {
  if (!products || products.length === 0) return;

  const doc    = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW  = doc.internal.pageSize.getWidth();
  const pageH  = doc.internal.pageSize.getHeight();
  const margin = 40;

  // ── Carrega logo do usuário, se houver ──
  let logoBase64 = null;
  if (user.image) {
    try {
      logoBase64 = await urlToBase64(user.image);
    } catch (e) {
      console.warn('Nao foi possivel carregar a logo:', e);
    }
  }

  // ── Pré-carrega imagens dos produtos em base64 (paralelo) ──
  const productImages = await Promise.all(
    products.map(async (p) => {
      if (!p.image) return null;
      try {
        return await urlToBase64(p.image);
      } catch {
        return null;
      }
    })
  );

  const headerH = drawPageHeader(doc, user, pageW, logoBase64);

  let y = headerH + 16;

  // ══════════════════════════════════════════════════════════════════════
  // DADOS DA EMPRESA / EMITENTE
  // ══════════════════════════════════════════════════════════════════════
  y = sectionHeader(doc, 'Dados da Empresa', y, pageW);

  const colMid = pageW / 2;
  const col2x  = colMid + 10;
  const colW   = colMid - margin - 20;

  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); setColor(doc, COLORS.muted);
  doc.text('NOME / RAZAO SOCIAL', margin + 8, y);
  doc.text('CNPJ', col2x, y);
  y += 11;

  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); setColor(doc, COLORS.dark);
  doc.text(truncate(doc, safe(user.name) || '-', colW), margin + 8, y);
  doc.text(safe(user.cnpj) || '-', col2x, y);
  y += 14;

  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); setColor(doc, COLORS.muted);
  doc.text('ENDERECO', margin + 8, y);
  if (user.phone) doc.text('TELEFONE', col2x, y);
  y += 11;

  const address = user.localization
    ? [user.localization.address, user.localization.city, user.localization.state]
        .filter(Boolean).join(', ')
    : '';

  doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5); setColor(doc, COLORS.dark);
  doc.text(truncate(doc, safe(address) || '-', colW), margin + 8, y);
  if (user.phone) doc.text(safe(user.phone), col2x, y);
  y += 22;

  // ══════════════════════════════════════════════════════════════════════
  // LISTAGEM DE PRODUTOS (cards verticais com foto + dados)
  // ══════════════════════════════════════════════════════════════════════
  y = sectionHeader(doc, `Produtos (${products.length})`, y, pageW);

  const cardH      = 78;
  const imgSize    = 58;
  const tableW     = pageW - margin * 2;
  const footerSafe = pageH - 50; // espaço reservado para o rodapé

  let pageNum = 1;

  products.forEach((product, i) => {
    // Quebra de página se não houver espaço suficiente
    if (y + cardH > footerSafe) {
      doc.addPage();
      pageNum++;
      drawPageHeader(doc, user, pageW, logoBase64);
      y = (logoBase64 ? 90 : 72) + 16;
      y = sectionHeader(doc, `Produtos (continuacao)`, y, pageW);
    }

    const isEven = i % 2 === 0;
    setColor(doc, isEven ? COLORS.white : COLORS.lightAlt, 'fill');
    doc.rect(margin, y, tableW, cardH, 'F');

    setColor(doc, COLORS.border, 'draw');
    doc.setLineWidth(0.3);
    doc.rect(margin, y, tableW, cardH);

    // ── Foto do produto ──
    const imgX = margin + 10;
    const imgY = y + (cardH - imgSize) / 2;
    const base64Img = productImages[i];

    setColor(doc, COLORS.lightAlt, 'fill');
    setColor(doc, COLORS.border, 'draw');
    doc.roundedRect(imgX, imgY, imgSize, imgSize, 4, 4, 'FD');

    if (base64Img) {
      try {
        doc.addImage(base64Img, 'JPEG', imgX + 2, imgY + 2, imgSize - 4, imgSize - 4);
      } catch {
        // Se a imagem falhar (formato incompatível), mantém o placeholder
      }
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      setColor(doc, COLORS.muted);
      doc.text('SEM FOTO', imgX + imgSize / 2, imgY + imgSize / 2 + 3, { align: 'center' });
    }

    // ── Dados do produto ──
    const textX = imgX + imgSize + 16;
    const textW = tableW - imgSize - 180;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    setColor(doc, COLORS.dark);
    doc.text(truncate(doc, safe(product.name) || '-', textW), textX, y + 20);

    if (product.category) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      setColor(doc, COLORS.accent);
      doc.text(safe(product.category).toUpperCase(), textX, y + 33);
    }

    if (product.description) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      setColor(doc, COLORS.muted);
      const descLines = doc.splitTextToSize(safe(product.description), textW);
      doc.text(descLines.slice(0, 2), textX, y + 47);
    }

    // ── Estoque ──
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    setColor(doc, COLORS.muted);
    doc.text(`Estoque: ${product.stock ?? 0}`, textX, y + cardH - 10);

    // ── Preço (destaque à direita) ──
    const priceX = margin + tableW - 14;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    setColor(doc, COLORS.primary);
    doc.text(`R$ ${Number(product.price || 0).toFixed(2)}`, priceX, y + cardH / 2 + 5, { align: 'right' });

    y += cardH + 8;
  });

  // ── Aplica rodapé em todas as páginas ──
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    drawPageFooter(doc, user, pageW, pageH, p, totalPages);
  }

  // ══════════════════════════════════════════════════════════════════════
  // DOWNLOAD
  // ══════════════════════════════════════════════════════════════════════
  const fileName = `catalogo-produtos-${new Date().toISOString().slice(0, 10)}.pdf`;
  const blob     = doc.output('blob');
  const url      = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href     = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}